import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Paving Order Flow Component
 * Handles order type selection (Single Pallet vs Custom Area) and single pallet quick order flow
 */
export default class PavingOrderFlow extends Component {
  /** @type {any} */
  productData = null;
  /** @type {any[]} */
  productVariants = [];
  /** @type {Record<string, any>} */
  variantMap = {};
  /** @type {any[]} */
  deliveryVariants = [];
  /** @type {Record<string, Record<string, any>>} */
  deliveryVariantMap = {};
  
  /** @type {'full'|'half'|'quarter'|null} */
  selectedPackType = null;
  /** @type {any} */
  selectedVariant = null;
  /** @type {number} */
  quantity = 1;
  /** @type {{postcode:string, zone:string, type:string, date:string, cost:number}} */
  deliveryState = { postcode: '', zone: '', type: 'standard', date: '', cost: 0 };
  
  /** @type {boolean} */
  userManuallySelectedDeliveryType = false;
  
  /** @type {Map<string, string>} Map of blocked dates (YYYY-MM-DD) to holiday names */
  blockedHolidays = new Map();

  /** @type {ReturnType<typeof setTimeout>|null} */
  autoCheckTimeout = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  postcodeCheckTimeout = null;

  /** When false, Express is hidden and only Standard (weekday) / weekend options apply */
  expressAvailable = true;

  connectedCallback() {
    super.connectedCallback();
    
    // Parse product data
    const productDataJson = this.dataset.productData || '{}';
    try {
      this.productData = JSON.parse(productDataJson);
      this.productVariants = this.productData.variants || [];
      this.deliveryVariants = this.productData.deliveryVariants || [];
      this.expressAvailable = this.productData.expressAvailable === true;
      this.variantMap = this.organizeVariants(this.productVariants);
      this.deliveryVariantMap = this.organizeDeliveryVariants(this.deliveryVariants);
      
      // Parse blocked delivery holidays from store metafield
      this.parseBlockedHolidays();
      
      // Generate pack type options dynamically from available variants
      this.generatePackTypeOptions();
    } catch (e) {
      console.error('PavingOrderFlow: Failed to parse product data', e);
    }
  }

  /**
   * Organize variants by pack type
   * @param {any[]} variants
   */
  organizeVariants(variants) {
    /** @type {Record<string, any>} */
    const map = {};
    
    for (const variant of variants) {
      if (!variant) continue;
      const packType = this.derivePackType(variant);
      if (packType) {
        map[packType] = variant;
      }
    }
    
    return map;
  }

  /**
   * Derive pack type from variant
   * @param {any} variant
   */
  derivePackType(variant) {
    if (!variant) return null;
    
    const packSize = variant.metafields?.custom?.pack_size || variant.pack_size || '';
    if (packSize) {
      const normalized = String(packSize).toLowerCase().trim();
      if (normalized.includes('full')) return 'full';
      if (normalized.includes('half')) return 'half';
      if (normalized.includes('quarter')) return 'quarter';
    }
    
    const searchText = [
      variant.option1 || '',
      variant.option2 || '',
      variant.option3 || '',
      variant.title || ''
    ].join(' ').toLowerCase();
    
    if (searchText.includes('quarter')) return 'quarter';
    if (searchText.includes('half')) return 'half';
    if (searchText.includes('full')) return 'full';
    
    return null;
  }

  /**
   * Organize delivery variants by zone and type
   * @param {any[]} variants
   */
  organizeDeliveryVariants(variants) {
    /** @type {Record<string, Record<string, any>>} */
    const map = {};
    
    for (const variant of variants) {
      if (!variant) continue;
      const zone = (variant.zone || variant.option1 || '').trim();
      const type = (variant.type || variant.option2 || '').toLowerCase().trim();
      
      if (zone && type) {
        let normalizedType = type;
        if (type === 'saturday' || type === 'weekend') {
          normalizedType = 'weekend';
        }
        
        if (!map[zone]) map[zone] = {};
        map[zone][normalizedType] = variant;
      }
    }
    
    return map;
  }

  /**
   * Get delivery variant ID
   * @param {string} zone
   * @param {string} type
   */
  getDeliveryVariantId(zone, type) {
    if (!zone || !type) return null;
    const normalizedType = type.toLowerCase() === 'saturday' ? 'weekend' : type.toLowerCase();
    const zoneMap = this.deliveryVariantMap[zone];
    if (!zoneMap) return null;
    const variant = zoneMap[normalizedType];
    return variant ? variant.id : null;
  }

  /**
   * Show order type selection modal
   */
  showOrderTypeSelection() {
    try {
      // Try to get modal from refs first, then fallback to querySelector
      let modal = null;
      
      // Try refs
      if (this.refs && this.refs.orderTypeModal) {
        const refModal = this.refs.orderTypeModal;
        if (Array.isArray(refModal)) {
          modal = refModal[0];
        } else {
          modal = refModal;
        }
      }
      
      // Fallback to direct query
      if (!modal || !(modal instanceof HTMLDialogElement)) {
        modal = this.querySelector('dialog[ref="orderTypeModal"]');
      }
      
      // Final fallback - find first dialog in component
      if (!modal || !(modal instanceof HTMLDialogElement)) {
        const dialogs = this.querySelectorAll('dialog');
        if (dialogs.length > 0) {
          modal = dialogs[0];
        }
      }
      
      if (modal && modal instanceof HTMLDialogElement) {
        modal.showModal();
      } else {
        console.error('PavingOrderFlow: Order type modal not found', {
          refs: this.refs,
          dialogs: this.querySelectorAll('dialog').length
        });
      }
    } catch (error) {
      console.error('PavingOrderFlow: Error showing order type selection:', error);
    }
  }

  /**
   * Handle order type selection
   * @param {string} type
   */
  handleOrderTypeSelect(type) {
    if (type === 'single-pallet') {
      this.showPackTypeSelection();
    } else if (type === 'custom-area') {
      this.openCalculator();
    }
    this.closeOrderTypeModal();
  }

  /**
   * Generate pack type options dynamically from available variants
   * Uses variant titles/names from the product instead of hardcoded labels
   */
  generatePackTypeOptions() {
    const optionsContainer = this.refs.packTypeOptions;
    if (!optionsContainer || !(optionsContainer instanceof HTMLElement)) {
      return;
    }
    
    // Clear existing options
    optionsContainer.innerHTML = '';
    
    // Get all available pack types from variantMap
    // Sort pack types in a logical order: full, half, quarter
    const packTypeOrder = ['full', 'half', 'quarter'];
    const packTypes = Object.keys(this.variantMap);
    const sortedPackTypes = packTypes.sort((a, b) => {
      const indexA = packTypeOrder.indexOf(a);
      const indexB = packTypeOrder.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
    
    // Generate buttons for each available pack type using variant names
    for (const packTypeStr of sortedPackTypes) {
      // Type guard: ensure packType is one of the valid values
      if (packTypeStr !== 'full' && packTypeStr !== 'half' && packTypeStr !== 'quarter') {
        continue;
      }
      const packType = packTypeStr;
      const variant = this.variantMap[packType];
      if (!variant) continue;
      
      // Get display name from variant:
      // 1. Try variant title (full name)
      // 2. Try option1 (first option, often the pack size)
      // 3. Fallback to formatted pack type
      let displayName = variant.title || variant.option1 || '';
      
      // If title/option1 is empty or doesn't look like a proper name, use option1 + option2
      if (!displayName || displayName.trim() === '') {
        const options = [
          variant.option1 || '',
          variant.option2 || '',
          variant.option3 || ''
        ].filter(opt => opt && opt.trim() !== '').join(' / ');
        displayName = options || this.formatPackTypeLabel(packType);
      }
      
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'paving-order-modal__option';
      button.onclick = () => this.handlePackTypeSelect(packType);
      
      const titleSpan = document.createElement('span');
      titleSpan.className = 'paving-order-modal__option-title';
      titleSpan.textContent = displayName;
      
      button.appendChild(titleSpan);
      optionsContainer.appendChild(button);
    }
    
    // If no pack types available, show a message
    if (optionsContainer.children.length === 0) {
      const message = document.createElement('p');
      message.textContent = 'No pack types available for this product.';
      message.style.padding = '1rem';
      message.style.textAlign = 'center';
      message.style.color = 'rgb(var(--color-foreground-rgb) / 0.6)';
      optionsContainer.appendChild(message);
    }
  }

  /**
   * Format pack type as a label (fallback only)
   * @param {'full'|'half'|'quarter'} packType
   * @returns {string}
   */
  formatPackTypeLabel(packType) {
    const labels = {
      'full': 'Full pallet',
      'half': 'Half pallet',
      'quarter': 'Quarter pallet'
    };
    return labels[packType] || packType;
  }

  /**
   * Show pack type selection
   */
  showPackTypeSelection() {
    // Regenerate options in case variants changed
    this.generatePackTypeOptions();
    
    const modal = this.refs.packTypeModal;
    if (modal && modal instanceof HTMLDialogElement) {
      modal.showModal();
    }
  }

  /**
   * Handle pack type selection
   * @param {'full'|'half'|'quarter'} packType
   */
  handlePackTypeSelect(packType) {
    this.selectedPackType = packType;
    this.selectedVariant = this.variantMap[packType];
    this.quantity = 1; // Reset quantity when pack type changes
    
    if (!this.selectedVariant) {
      console.error('PavingOrderFlow: Variant not found for pack type', packType);
      return;
    }
    
    this.closePackTypeModal();
    this.showDeliveryCheck();
  }

  /**
   * Show delivery check form
   */
  showDeliveryCheck() {
    const modal = this.refs.deliveryModal;
    if (modal && modal instanceof HTMLDialogElement) {
      // Update selected pack display immediately
      this.updateSelectedPackDisplay();
      
      // Update product price in summary immediately
      this.updateDeliveryDisplay();
      
      // Reset delivery state
      this.deliveryState = { postcode: '', zone: '', type: 'standard', date: '', cost: 0 };
      this.clearDeliveryMessages();
      
      modal.showModal();
      
      // Set up auto-check on postcode input
      this.setupAutoDeliveryCheck();
      
      // Set up quantity input listener
      const quantityInput = this.querySelector('[ref="quantityInput"]') || 
                           (this.refs?.quantityInput && !Array.isArray(this.refs.quantityInput) ? this.refs.quantityInput : null);
      if (quantityInput && quantityInput instanceof HTMLInputElement) {
        quantityInput.value = String(this.quantity);
        quantityInput.addEventListener('change', () => {
          const value = parseInt(quantityInput.value, 10);
          if (!isNaN(value) && value >= 1) {
            this.quantity = value;
            // Recalculate delivery cost if delivery is already calculated
            // Note: Weekend delivery is flat rate, so cost doesn't change with quantity
            if (this.deliveryState.zone && this.deliveryState.postcode && this.deliveryState.date) {
              const isWeekend = this.deliveryState.type === 'weekend' || this.deliveryState.type === 'saturday';
              if (!isWeekend) {
                // Only recalculate for per-pack pricing (Standard/Express)
                const packCount = this.quantity;
                const deliveryCost = this.estimateDeliveryCost(this.deliveryState.type, this.deliveryState.zone, this.deliveryState.date, packCount);
                this.deliveryState.cost = deliveryCost;
              }
              // For weekend, cost stays at £75 flat
            }
            this.updateDeliveryDisplay();
          } else {
            quantityInput.value = String(this.quantity);
          }
        });
      }
      
      // Focus on date input and update delivery type options
      const dateInput = this.refs.deliveryDateInput;
      if (dateInput && !Array.isArray(dateInput) && dateInput instanceof HTMLInputElement) {
        // Initialize delivery type options (show standard/express, hide weekend if no date)
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          this.updateDeliveryTypeOptions(dateInput.value || '');
        }, 150);
        setTimeout(() => dateInput.focus(), 100);
      }
    }
  }

  /**
   * Update selected pack display
   */
  updateSelectedPackDisplay() {
    // Use querySelector as fallback for refs
    const packDisplay = this.querySelector('[ref="selectedPackDisplay"]') || 
                       (this.refs?.selectedPackDisplay && !Array.isArray(this.refs.selectedPackDisplay) ? this.refs.selectedPackDisplay : null);
    const packPriceDisplay = this.querySelector('[ref="selectedPackPriceDisplay"]') || 
                            (this.refs?.selectedPackPriceDisplay && !Array.isArray(this.refs.selectedPackPriceDisplay) ? this.refs.selectedPackPriceDisplay : null);
    
    if (packDisplay && packDisplay instanceof HTMLElement) {
      const packTypeLabel = this.selectedPackType 
        ? this.selectedPackType.charAt(0).toUpperCase() + this.selectedPackType.slice(1) + ' pallet'
        : 'Not selected';
      packDisplay.textContent = packTypeLabel;
    }
    
    if (packPriceDisplay && packPriceDisplay instanceof HTMLElement && this.selectedVariant) {
      const price = (this.selectedVariant.price || 0) * this.quantity;
      packPriceDisplay.textContent = this.formatPrice(price);
    }
    
    // Also update the summary section product price
    this.updateDeliveryDisplay();
  }

  /**
   * Increase quantity
   */
  increaseQuantity() {
    this.quantity = Math.max(1, this.quantity + 1);
    this.updateQuantityDisplay();
    // Recalculate delivery cost if delivery is already calculated
    // Note: Weekend delivery is flat rate, so cost doesn't change with quantity
    if (this.deliveryState.zone && this.deliveryState.postcode && this.deliveryState.date) {
      const isWeekend = this.deliveryState.type === 'weekend' || this.deliveryState.type === 'saturday';
      if (!isWeekend) {
        // Only recalculate for per-pack pricing (Standard/Express)
        const packCount = this.quantity;
        const deliveryCost = this.estimateDeliveryCost(this.deliveryState.type, this.deliveryState.zone, this.deliveryState.date, packCount);
        this.deliveryState.cost = deliveryCost;
      }
      // For weekend, cost stays at £75 flat
    }
    this.updateDeliveryDisplay();
  }

  /**
   * Decrease quantity
   */
  decreaseQuantity() {
    this.quantity = Math.max(1, this.quantity - 1);
    this.updateQuantityDisplay();
    // Recalculate delivery cost if delivery is already calculated
    // Note: Weekend delivery is flat rate, so cost doesn't change with quantity
    if (this.deliveryState.zone && this.deliveryState.postcode && this.deliveryState.date) {
      const isWeekend = this.deliveryState.type === 'weekend' || this.deliveryState.type === 'saturday';
      if (!isWeekend) {
        // Only recalculate for per-pack pricing (Standard/Express)
        const packCount = this.quantity;
        const deliveryCost = this.estimateDeliveryCost(this.deliveryState.type, this.deliveryState.zone, this.deliveryState.date, packCount);
        this.deliveryState.cost = deliveryCost;
      }
      // For weekend, cost stays at £75 flat
    }
    this.updateDeliveryDisplay();
  }

  /**
   * Update quantity display
   */
  updateQuantityDisplay() {
    const quantityInput = this.querySelector('[ref="quantityInput"]') || 
                         (this.refs?.quantityInput && !Array.isArray(this.refs.quantityInput) ? this.refs.quantityInput : null);
    if (quantityInput && quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(this.quantity);
    }
  }

  /**
   * Setup auto-check delivery on postcode input
   */
  setupAutoDeliveryCheck() {
    // Use setTimeout to ensure refs are ready
    setTimeout(() => {
      // Get inputs with fallback to querySelector
      const postcodeInput = (this.refs?.deliveryPostcodeInput && !Array.isArray(this.refs.deliveryPostcodeInput) ? this.refs.deliveryPostcodeInput : null) ||
                           this.querySelector('[ref="deliveryPostcodeInput"]') ||
                           this.querySelector('input[type="text"][placeholder*="postcode" i]');
      const dateInput = (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null) ||
                       this.querySelector('[ref="deliveryDateInput"]') ||
                       this.querySelector('input[type="date"]');
      const typeSelect = (this.refs?.deliveryTypeSelect && !Array.isArray(this.refs.deliveryTypeSelect) ? this.refs.deliveryTypeSelect : null) ||
                        this.querySelector('[ref="deliveryTypeSelect"]') ||
                        this.querySelector('select');
      
      // Helper to get current values
      const getCurrentValues = () => {
        const postcode = postcodeInput && postcodeInput instanceof HTMLInputElement
          ? postcodeInput.value.trim()
          : '';
        const date = dateInput && dateInput instanceof HTMLInputElement
          ? dateInput.value
          : '';
        const type = typeSelect && typeSelect instanceof HTMLSelectElement
          ? typeSelect.value
          : 'standard';
        return { postcode, date, type };
      };
      
      // Helper to trigger check when postcode is complete
      const triggerCheckIfReady = (immediate = false) => {
        clearTimeout(this.autoCheckTimeout);
        const delay = immediate ? 0 : 350;

        this.autoCheckTimeout = setTimeout(() => {
          const { postcode, date } = getCurrentValues();
          if (!date) return;

          const cleaned = this.sanitizePostcode(postcode);
          if (!cleaned) return;

          if (this.isValidUKPostcodeFormat(cleaned)) {
            this.handleDeliveryCheck();
          }
        }, delay);
      };
      
      if (postcodeInput && postcodeInput instanceof HTMLInputElement) {
        postcodeInput.addEventListener('input', () => {
          this.normalizePostcodeInputValue(postcodeInput);
          this.hideDeliveryTypeFeedback();
          triggerCheckIfReady();
        });

        postcodeInput.addEventListener('paste', () => {
          requestAnimationFrame(() => {
            this.normalizePostcodeInputValue(postcodeInput, { formatSpace: true });
            this.hideDeliveryTypeFeedback();
            triggerCheckIfReady(true);
          });
        });

        postcodeInput.addEventListener('blur', () => {
          this.normalizePostcodeInputValue(postcodeInput, { formatSpace: true });
          const { postcode, date } = getCurrentValues();
          if (!postcode || !date) return;

          const cleaned = this.sanitizePostcode(postcode);
          if (this.isValidUKPostcodeFormat(cleaned)) {
            this.handleDeliveryCheck();
          } else {
            this.setDeliveryError('Invalid postcode format. Please enter a valid UK postcode (e.g., SW1A 1AA).');
          }
        });
      }
      
      if (dateInput && dateInput instanceof HTMLInputElement) {
        // Initialize delivery type options based on current date value (if any)
        const currentDate = dateInput.value || '';
        setTimeout(() => {
          this.updateDeliveryTypeOptions(currentDate);
        }, 100);
        
        // Update delivery type options and auto-select based on date
        dateInput.addEventListener('input', () => {
          const { date, postcode } = getCurrentValues();
          // Validate date (prevent Sundays, too soon, blocked holidays)
          if (!this.validateDeliveryDate(date || '')) {
            return;
          }
          setTimeout(() => {
            this.syncTypeWithDate(date || '');
            if (postcode && date) {
              this.handleDeliveryCheck();
            }
          }, 50);
        });
        
        // Auto-check when date changes
        dateInput.addEventListener('change', () => {
          const { date, postcode } = getCurrentValues();
          // Validate date (prevent Sundays, too soon, blocked holidays)
          if (!this.validateDeliveryDate(date || '')) {
            return;
          }
          setTimeout(() => {
            this.syncTypeWithDate(date || '');
            if (postcode && date) {
              this.handleDeliveryCheck();
            }
          }, 50);
        });
        
        // Earliest selectable date: express window when Express is offered, else standard lead time only
        dateInput.setAttribute('min', this.getEffectiveMinimumDeliveryDateString());
      }
      
      if (typeSelect && typeSelect instanceof HTMLSelectElement) {
        // Make the select field read-only (disabled but visible)
        typeSelect.disabled = true;
        typeSelect.style.cursor = 'not-allowed';
        typeSelect.style.opacity = '0.7';
        typeSelect.style.pointerEvents = 'none'; // Prevent any interaction
        typeSelect.setAttribute('aria-readonly', 'true');
        typeSelect.setAttribute('tabindex', '-1'); // Remove from tab order
        typeSelect.title = 'Delivery method is automatically selected based on your delivery date';
        
        // Prevent any click/change events
        typeSelect.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        typeSelect.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        typeSelect.addEventListener('change', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }
    }, 100);
  }

  /**
   * Handle delivery check
   */
  async handleDeliveryCheck() {
    // Get current values from DOM directly using querySelector (more reliable than refs)
    const postcodeInput = this.querySelector('[ref="deliveryPostcodeInput"]') ||
                         this.querySelector('input[type="text"][placeholder*="postcode" i]') ||
                         (this.refs?.deliveryPostcodeInput && !Array.isArray(this.refs.deliveryPostcodeInput) ? this.refs.deliveryPostcodeInput : null);
    const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                     this.querySelector('input[type="date"]') ||
                     (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
    const typeSelect = this.querySelector('[ref="deliveryTypeSelect"]') ||
                      this.querySelector('select') ||
                      (this.refs?.deliveryTypeSelect && !Array.isArray(this.refs.deliveryTypeSelect) ? this.refs.deliveryTypeSelect : null);
    
    if (!postcodeInput || !dateInput || !typeSelect) {
      console.warn('PavingOrderFlow: Could not find delivery input elements');
      return;
    }
    
    const postcode = this.sanitizePostcode(
      (postcodeInput instanceof HTMLInputElement ? postcodeInput.value : '') || ''
    );
    const date = (dateInput instanceof HTMLInputElement ? dateInput.value : '') || '';
    const type = (typeSelect instanceof HTMLSelectElement ? typeSelect.value : '') || 'standard';
    
    await this.processDeliveryCheck(postcode, date, type);
  }

  /**
   * Process delivery check with values
   * @param {string} postcode
   * @param {string} date
   * @param {string} type
   */
  async processDeliveryCheck(postcode, date, type) {
    // Clear previous messages
    this.clearDeliveryMessages();
    
    // Show loading state
    this.setDeliveryLoading(true);
    
    // Small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (!postcode || !date) {
      this.setDeliveryLoading(false);
      // Don't show error if fields are just empty (user is still typing)
      if (postcode.length > 0 || date) {
        this.setDeliveryError('Please enter postcode and select delivery date.');
      }
      return;
    }
    
    if (!this.isValidUKPostcodeFormat(postcode)) {
      this.setDeliveryLoading(false);
      this.setDeliveryError('Invalid postcode format. Please enter a valid UK postcode (e.g., SW1A 1AA).');
      return;
    }
    
    const zone = this.determineZone(postcode);
    if (!zone) {
      this.setDeliveryLoading(false);
      this.setDeliveryError('Postcode not recognized. Please check and try again.');
      return;
    }
    
    if (zone === 'Zone 4') {
      this.setDeliveryLoading(false);
      this.setDeliveryError('Delivery not available to this area. Please contact us for shipping options.');
      
      // Disable add to cart for Zone 4
      this.deliveryState = {
        postcode,
        zone: 'Zone 4',
        type: '',
        date,
        cost: 0
      };
      this.updateDeliveryDisplay();
      
      // Disable add to cart button
      const addToCartBtn = this.querySelector('[ref="addToCartBtn"]') || 
                          (this.refs?.addToCartBtn && !Array.isArray(this.refs.addToCartBtn) ? this.refs.addToCartBtn : null);
      if (addToCartBtn && addToCartBtn instanceof HTMLButtonElement) {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('is-disabled');
      }
      
      return;
    }
    
    // Normalize delivery type - match calculator behavior
    let normalizedType = type.toLowerCase();
    if (normalizedType === 'saturday') {
      normalizedType = 'weekend';
    }
    
    // Match calculator behavior: auto-sync type if date is weekend
    // This ensures weekend dates always use weekend pricing (same as calculator)
    const isWeekend = this.isWeekendDate(date);
    const effectiveType = isWeekend ? 'weekend' : normalizedType;
    
    // Update delivery type options based on date
    this.updateDeliveryTypeOptions(date);
    
    // If date is weekend, auto-update the select to match (same as calculator)
    if (isWeekend && normalizedType !== 'weekend') {
      const typeSelect = this.refs?.deliveryTypeSelect && !Array.isArray(this.refs.deliveryTypeSelect) 
        ? this.refs.deliveryTypeSelect 
        : this.querySelector('[ref="deliveryTypeSelect"]');
      if (typeSelect && typeSelect instanceof HTMLSelectElement) {
        typeSelect.value = 'weekend';
      }
    }
    
    // Calculate delivery cost using effective type (same logic as calculator)
    // For single pallet, quantity = number of packs
    const packCount = this.quantity; // Quantity = number of packs
    const deliveryCost = this.estimateDeliveryCost(effectiveType, zone, date, packCount);
    
    this.deliveryState = {
      postcode,
      zone,
      type: effectiveType, // Use effective type (may be auto-synced to weekend)
      date,
      cost: deliveryCost
    };
    
    this.setDeliveryLoading(false);
    this.updateDeliveryDisplay();
    
    const deliveryTypeLabel = normalizedType === 'standard' ? 'Standard' : normalizedType === 'express' ? 'Express' : 'Saturday';
    const costText = deliveryCost > 0 ? this.formatPrice(deliveryCost) : 'Free';
    this.setDeliverySuccess(`✓ Delivery available in ${zone} (${deliveryTypeLabel}) - ${costText}`);
  }

  /**
   * Set delivery loading state
   * @param {boolean} isLoading
   */
  setDeliveryLoading(isLoading) {
    const loadingEl = this.refs.deliveryLoading;
    if (loadingEl && loadingEl instanceof HTMLElement) {
      loadingEl.style.display = isLoading ? 'block' : 'none';
    }
  }

  /**
   * Clear delivery messages
   */
  clearDeliveryMessages() {
    const errorEl = this.refs.deliveryError;
    const successEl = this.refs.deliverySuccess;
    
    if (errorEl && errorEl instanceof HTMLElement) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    }
    if (successEl && successEl instanceof HTMLElement) {
      successEl.style.display = 'none';
      successEl.textContent = '';
    }
  }

  /**
   * Sanitize postcode
   * @param {string} value
   */
  sanitizePostcode(value) {
    return (value || '').trim().toUpperCase();
  }

  /**
   * Keep postcode uppercase while typing without disrupting cursor focus.
   * @param {HTMLInputElement} input
   * @param {{formatSpace?: boolean}} options
   * @returns {string}
   */
  normalizePostcodeInputValue(input, { formatSpace = false } = {}) {
    if (!(input instanceof HTMLInputElement)) return '';

    const selectionStart = input.selectionStart ?? input.value.length;
    const alphanumBeforeCursor = input.value
      .slice(0, selectionStart)
      .replace(/[^A-Za-z0-9]/g, '').length;

    const alphanum = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    let formatted = alphanum;

    if (formatSpace && alphanum.length > 4) {
      formatted = `${alphanum.slice(0, -3)} ${alphanum.slice(-3)}`;
    } else if (!formatSpace) {
      formatted = input.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ');
    }

    if (formatted !== input.value) {
      input.value = formatted;
      let newCursor = 0;
      let seen = 0;
      for (let i = 0; i < formatted.length; i++) {
        const char = formatted.charAt(i);
        if (/[A-Z0-9]/.test(char)) {
          seen++;
        }
        newCursor = i + 1;
        if (seen >= alphanumBeforeCursor) {
          break;
        }
      }
      input.setSelectionRange(newCursor, newCursor);
    }

    return formatted.trim();
  }

  /**
   * Validate UK postcode format
   */
  isValidUKPostcodeFormat(postcode = '') {
    if (!postcode) return false;
    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, '');
    const ukPostcodePattern = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/;
    return ukPostcodePattern.test(cleaned);
  }

  /**
   * Extract postcode prefix
   */
  extractPostcodePrefix(postcode = '') {
    if (!postcode) return null;
    if (!this.isValidUKPostcodeFormat(postcode)) return null;
    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, '');
    const match = cleaned.match(/^([A-Z]{1,2})\d/);
    return match && match[1] ? match[1] : null;
  }

  /**
   * Determine zone from postcode
   */
  determineZone(postcode = '') {
    const prefix = this.extractPostcodePrefix(postcode);
    if (!prefix) return null;
    
    const zones = this.getUKPostcodeZones();
    if (prefix.length >= 2 && zones[prefix]) {
      return zones[prefix];
    }
    if (prefix.length === 1 && zones[prefix]) {
      return zones[prefix];
    }
    return null;
  }

  /**
   * Get UK postcode zones (same as product-calculator.js)
   * @returns {Record<string, string>}
   */
  getUKPostcodeZones() {
    /** @type {Record<string, string>} */
    return {
'AL': 'Zone 1', 'B': 'Zone 1', 'BA': 'Zone 1', 'BB': 'Zone 1', 'BD': 'Zone 1', 'BH': 'Zone 1', 'BL': 'Zone 1', 'BR': 'Zone 1', 'BS': 'Zone 1',
      'CB': 'Zone 1', 'CF': 'Zone 1', 'CH': 'Zone 1', 'CM': 'Zone 1', 'CO': 'Zone 1', 'CR': 'Zone 1', 'CV': 'Zone 1', 'CW': 'Zone 1',
      'DA': 'Zone 1', 'DE': 'Zone 1', 'DH': 'Zone 1', 'DL': 'Zone 1', 'DN': 'Zone 1', 'DY': 'Zone 1',
      'EN': 'Zone 1', 'EX': 'Zone 1',
      'FY': 'Zone 1',
      'GL': 'Zone 1', 'GU': 'Zone 1',
      'HA': 'Zone 1', 'HD': 'Zone 1', 'HG': 'Zone 1', 'HP': 'Zone 1', 'HR': 'Zone 1', 'HU': 'Zone 1', 'HX': 'Zone 1',
      'IG': 'Zone 1', 'IP': 'Zone 1',
      'KT': 'Zone 1',
      'L': 'Zone 1', 'LE': 'Zone 1', 'LN': 'Zone 1', 'LS': 'Zone 1', 'LU': 'Zone 1',
      'M': 'Zone 1', 'ME': 'Zone 1', 'MK': 'Zone 1',
      'NG': 'Zone 1', 'NN': 'Zone 1', 'NP': 'Zone 1', 'NR': 'Zone 1',
      'OL': 'Zone 1', 'OX': 'Zone 1',
      'PE': 'Zone 1', 'PO': 'Zone 1', 'PR': 'Zone 1',
      'RG': 'Zone 1', 'RH': 'Zone 1', 'RM': 'Zone 1',
      'S': 'Zone 1', 'SG': 'Zone 1', 'SK': 'Zone 1', 'SL': 'Zone 1', 'SM': 'Zone 1', 'SN': 'Zone 1', 'SO': 'Zone 1', 'SP': 'Zone 1', 'SR': 'Zone 1', 'SS': 'Zone 1', 'ST': 'Zone 1',
      'TA': 'Zone 1', 'TF': 'Zone 1', 'TS': 'Zone 1', 'TW': 'Zone 1',
      'UB': 'Zone 1',
      'WA': 'Zone 1', 'WD': 'Zone 1', 'WF': 'Zone 1', 'WN': 'Zone 1', 'WR': 'Zone 1', 'WS': 'Zone 1', 'WV': 'Zone 1',
      'YO': 'Zone 1',
      'BN': 'Zone 2', 'CA': 'Zone 2', 'CT': 'Zone 2', 'DT': 'Zone 2', 'E': 'Zone 2', 'EC': 'Zone 2', 'LA': 'Zone 2',
      'LD': 'Zone 2', 'LL': 'Zone 2', 'N': 'Zone 2', 'NE': 'Zone 2', 'NW': 'Zone 2', 'PL': 'Zone 2', 'SA': 'Zone 2',
      'SE': 'Zone 2', 'SW': 'Zone 2', 'SY': 'Zone 2', 'TN': 'Zone 2', 'TQ': 'Zone 2', 'W': 'Zone 2', 'WC': 'Zone 2',
      'DG': 'Zone 3', 'EH': 'Zone 3', 'FK': 'Zone 3', 'G': 'Zone 3', 'KA': 'Zone 3',
      'KY': 'Zone 3', 'ML': 'Zone 3', 'PA': 'Zone 3', 'TD': 'Zone 3', 'TR': 'Zone 3',
      'AB': 'Zone 4', 'BT': 'Zone 4', 'DD': 'Zone 4', 'EIRE': 'Zone 4', 'GY': 'Zone 4',
      'HS': 'Zone 4', 'IM': 'Zone 4', 'IV': 'Zone 4', 'JE': 'Zone 4', 'KW': 'Zone 4',
      'PH': 'Zone 4', 'ZE': 'Zone 4'
    };
  }

  /**
   * Estimate delivery cost
   * Uses prices from the delivery product variants (delivery-paving-slabs)
   * Pricing is dynamically loaded from Shopify delivery product variants, not hardcoded.
   * 
   * Pricing structure (example - actual prices come from delivery product):
   * - Zone 1: Free (Standard), Express per pack, Weekend flat rate
   * - Zone 2: Standard per pack, Express per pack, Weekend flat rate
   * - Zone 3: Standard per pack, Express per pack, Weekend flat rate
   * - Zone 4: Not available (returns 0, should show "Contact Us")
   * 
   * Weekend/Saturday delivery uses variant price as flat rate (not multiplied by pack count).
   * Standard/Express delivery uses variant price × pack count.
   * 
   * This matches product-calculator.js exactly:
   * - If type is 'weekend'/'saturday' OR date is weekend, apply weekend pricing
   * - Otherwise use the selected type (standard/express)
   * 
   * @param {string} type - Delivery type: 'standard', 'express', 'weekend'/'saturday'
   * @param {string} zone - Zone name: 'Zone 1', 'Zone 2', 'Zone 3', 'Zone 4'
   * @param {string} date - Selected delivery date (YYYY-MM-DD)
   * @param {number} packCount - Number of packs (1 for single pallet)
   * @returns {number} Delivery cost in pence
   */
  estimateDeliveryCost(type, zone, date, packCount) {
    // Zone 4 - Not available
    if (zone === 'Zone 4') {
      return 0; // Will show "Contact Us" message instead
    }

    // Get total pack count (1 pack = 1 variant for single pallet)
    if (packCount === 0) return 0;

    let lowered = (type || '').toLowerCase();
    if (!this.expressAvailable && lowered === 'express') {
      lowered = 'standard';
    }
    const isWeekend = this.isWeekendDate(date);
    const effectiveType = (lowered === 'weekend' || lowered === 'saturday' || isWeekend) ? 'weekend' : lowered;
    
    // Use delivery variant price from configured delivery product
    const normalizedType = effectiveType === 'weekend' ? 'weekend' : effectiveType;
    const zoneMap = this.deliveryVariantMap[zone];
    if (zoneMap && zoneMap[normalizedType] && zoneMap[normalizedType].price !== undefined) {
      const variantPrice = zoneMap[normalizedType].price;
      
      // For weekend/Saturday, use variant price as flat rate (not multiplied by pack count)
      // This uses the actual price configured in the delivery product variant
      if (normalizedType === 'weekend') {
        return variantPrice; // Flat rate from variant (should be £75 = 7500 pence)
      }
      
      // For other types, use variant price multiplied by pack count
      return variantPrice * packCount;
    }
    
    // Fallback to calculated pricing (if variant not found)
    // Saturday/Weekend delivery - £75 flat for ALL zones
    if (normalizedType === 'weekend') {
      return 7500; // £75 flat rate for all zones
    }

    // Express delivery
    if (normalizedType === 'express') {
      let costPerPack = 2000; // £20 per pack base
      
      if (zone === 'Zone 2') {
        costPerPack = 3000; // £30 per pack (£10 zone + £20 express)
      } else if (zone === 'Zone 3') {
        costPerPack = 4000; // £40 per pack (£20 zone + £20 express)
      }
      // Zone 1: £20 per pack only
      
      return costPerPack * packCount;
    }

    // Standard delivery (default)
    if (zone === 'Zone 1') {
      return 0; // Free delivery
    } else if (zone === 'Zone 2') {
      return 1000 * packCount; // £10 per pack
    } else if (zone === 'Zone 3') {
      return 2000 * packCount; // £20 per pack
    }

    // Default: free (shouldn't reach here)
    return 0;
  }

  /**
   * Check if date is weekend
   */
  isWeekendDate(dateStr = '') {
    if (!dateStr) return false;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return false;
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  /**
   * Calculate minimum delivery dates for standard and express delivery
   * Standard: Today + 1 day pick/pack + 2 working days delivery = 3 working days minimum
   * Express: Today + 1 day pick/pack + 1 working day delivery = 2 working days minimum
   * @returns {{standard: string, express: string}} Date strings in YYYY-MM-DD format
   */
  calculateMinimumDeliveryDates() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Pick/pack day should be next working day (not weekend)
    let pickPackDate = new Date(today);
    pickPackDate.setDate(today.getDate() + 1);
    
    // If tomorrow is weekend, move to next Monday
    while (!this.isWorkingDay(pickPackDate)) {
      pickPackDate.setDate(pickPackDate.getDate() + 1);
    }
    
    // Express: 1 working day delivery after pick/pack
    const expressDate = this.addWorkingDays(pickPackDate, 1);
    
    // Standard: 2 working days delivery after pick/pack
    const standardDate = this.addWorkingDays(pickPackDate, 2);
    
    const formatDate = (/** @type {Date} */ date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      standard: formatDate(standardDate),
      express: formatDate(expressDate)
    };
  }

  /**
   * Earliest calendar date allowed in the date picker (express vs standard-only)
   * @returns {string} YYYY-MM-DD
   */
  getEffectiveMinimumDeliveryDateString() {
    const minDates = this.calculateMinimumDeliveryDates();
    return this.expressAvailable ? minDates.express : minDates.standard;
  }

  /**
   * Check if a date is a working day (Monday-Friday)
   * @param {Date} date
   * @returns {boolean}
   */
  isWorkingDay(date) {
    const day = date.getDay();
    return day >= 1 && day <= 5; // Monday (1) to Friday (5)
  }

  /**
   * Add working days to a date (excludes weekends)
   * @param {Date} startDate
   * @param {number} workingDays
   * @returns {Date}
   */
  addWorkingDays(startDate, workingDays) {
    const result = new Date(startDate);
    let daysAdded = 0;
    
    while (daysAdded < workingDays) {
      result.setDate(result.getDate() + 1);
      if (this.isWorkingDay(result)) {
        daysAdded++;
      }
    }
    
    return result;
  }

  /**
   * Get day of week (0 = Sunday, 6 = Saturday)
   * @param {string} dateStr
   * @returns {number}
   */
  getDayOfWeek(dateStr) {
    if (!dateStr) return -1;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return -1;
    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    return date.getDay();
  }

  /**
   * Parse date string to Date object
   * @param {string} dateStr
   * @returns {Date|null}
   */
  parseDate(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  /**
   * Sync delivery type select based on selected date with auto-logic
   * - If date < standardMinDate: Auto-select Express
   * - If date >= standardMinDate: Use Standard
   * - If date is Saturday/Sunday: Auto-select Saturday
   * @param {string} selectedDate
   */
  syncTypeWithDate(selectedDate) {
    const typeSelect = this.refs?.deliveryTypeSelect && !Array.isArray(this.refs.deliveryTypeSelect) 
      ? this.refs.deliveryTypeSelect 
      : this.querySelector('[ref="deliveryTypeSelect"]');
    
    if (!typeSelect || !(typeSelect instanceof HTMLSelectElement)) return;

    // Update available options based on date
    this.updateDeliveryTypeOptions(selectedDate);

    if (!selectedDate) {
      // If no date selected, keep current selection or default to standard
      if (!typeSelect.value) {
        typeSelect.value = 'standard';
        this.deliveryState.type = 'standard';
      }
      return;
    }

    const isWeekend = this.isWeekendDate(selectedDate);
    const isSunday = this.isWeekendDate(selectedDate) && this.getDayOfWeek(selectedDate) === 0;

    // Disable Sundays - they should not be selectable
    if (isSunday) {
      const dateInput = this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput)
        ? this.refs.deliveryDateInput
        : this.querySelector('[ref="deliveryDateInput"]');
      if (dateInput && dateInput instanceof HTMLInputElement) {
        dateInput.value = '';
      }
      return;
    }

    // Calculate minimum dates
    const minDates = this.calculateMinimumDeliveryDates();
    const selectedDateObj = this.parseDate(selectedDate);
    const standardMinDateObj = this.parseDate(minDates.standard);

    // Always auto-select based on date (no manual selection allowed)
    let autoSelectedType = null;
    let feedbackMessage = '';

    if (isWeekend) {
      // Weekend date: Auto-select Saturday delivery
      const weekendOption = Array.from(typeSelect.options).find(opt => opt.value === 'weekend' || opt.value === 'saturday');
      if (weekendOption) {
        autoSelectedType = weekendOption.value;
        feedbackMessage = 'Based on your selected delivery date Saturday delivery is applied.';
      }
    } else if (selectedDateObj && standardMinDateObj && selectedDateObj < standardMinDateObj) {
      // Date is earlier than standard minimum: Auto-select Express when offered
      if (this.expressAvailable) {
        const expressOption = Array.from(typeSelect.options).find(opt => opt.value === 'express');
        if (expressOption) {
          autoSelectedType = 'express';
          feedbackMessage = 'Based on your selected delivery date Express delivery is applied.';
        }
      } else {
        const standardOption = Array.from(typeSelect.options).find(opt => opt.value === 'standard');
        if (standardOption) {
          autoSelectedType = 'standard';
          feedbackMessage = 'Based on your selected delivery date Standard delivery is applied.';
        }
      }
    } else {
      // Date is >= standard minimum: Auto-select Standard
      const standardOption = Array.from(typeSelect.options).find(opt => opt.value === 'standard');
      if (standardOption) {
        autoSelectedType = 'standard';
        feedbackMessage = 'Based on your selected delivery date Standard delivery is applied.';
      }
    }

    // Always apply auto-selection
    if (autoSelectedType) {
      const typeChanged = typeSelect.value !== autoSelectedType;
      
      if (typeChanged) {
        typeSelect.value = autoSelectedType;
        this.deliveryState.type = autoSelectedType;
        
        // Trigger delivery check if zone is already set
        if (this.deliveryState.zone && this.deliveryState.postcode) {
          this.handleDeliveryCheck();
        }
      }
      
      // Always show feedback message when date is selected (even if type didn't change)
      if (feedbackMessage) {
        this.showDeliveryTypeFeedback(feedbackMessage);
      }
    }
  }

  /**
   * Show UX feedback message for auto-selected delivery type
   * @param {string} message
   */
  showDeliveryTypeFeedback(message) {
    // Find the date input field and its parent container
    const dateInput = this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput)
      ? this.refs.deliveryDateInput
      : this.querySelector('[ref="deliveryDateInput"]') ||
        this.querySelector('input[type="date"]');
    
    if (!dateInput || !(dateInput instanceof HTMLElement)) {
      console.warn('PavingOrderFlow: Could not find deliveryDateInput for feedback');
      return;
    }
    
    // Find the parent field container (paving-order-modal__field)
    const dateFieldContainer = dateInput.closest('.paving-order-modal__field');
    if (!dateFieldContainer || !(dateFieldContainer instanceof HTMLElement)) {
      console.warn('PavingOrderFlow: Could not find date field container for feedback');
      return;
    }
    
    // Create or update feedback element - insert after the hint
    let feedbackEl = dateFieldContainer.querySelector('.delivery-type-feedback');
    if (!feedbackEl) {
      feedbackEl = document.createElement('div');
      feedbackEl.className = 'delivery-type-feedback';
      if (feedbackEl instanceof HTMLElement) {
        feedbackEl.style.cssText = 'margin-top: 0.5rem; font-size: 0.875rem; padding: 0.5rem; border-radius: 4px; background-color: rgba(59, 130, 246, 0.1); color: rgb(59, 130, 246); border: 1px solid rgba(59, 130, 246, 0.2);';
      }
      
      // Insert after the hint (small tag)
      const hintEl = dateFieldContainer.querySelector('.paving-order-modal__hint') ||
                     dateFieldContainer.querySelector('small');
      if (hintEl) {
        if (hintEl.nextSibling) {
          dateFieldContainer.insertBefore(feedbackEl, hintEl.nextSibling);
        } else {
          dateFieldContainer.appendChild(feedbackEl);
        }
      } else {
        // If no hint, insert after date input
        if (dateInput.nextSibling) {
          dateFieldContainer.insertBefore(feedbackEl, dateInput.nextSibling);
        } else {
          dateFieldContainer.appendChild(feedbackEl);
        }
      }
    }
    
    if (feedbackEl && feedbackEl instanceof HTMLElement) {
      feedbackEl.textContent = message;
    }
    
    // Message stays visible - no auto-hide
    // Only hide when postcode is entered (handled in hideDeliveryTypeFeedback)
  }

  /**
   * Hide delivery type feedback message
   */
  hideDeliveryTypeFeedback() {
    // Clear timeout if exists
    if (this.deliveryTypeFeedbackTimeout) {
      clearTimeout(this.deliveryTypeFeedbackTimeout);
      this.deliveryTypeFeedbackTimeout = null;
    }
    
    // Find and remove feedback element from date field container
    const dateInput = this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput)
      ? this.refs.deliveryDateInput
      : this.querySelector('[ref="deliveryDateInput"]') ||
        this.querySelector('input[type="date"]');
    
    if (dateInput && dateInput instanceof HTMLElement) {
      const dateFieldContainer = dateInput.closest('.paving-order-modal__field');
      if (dateFieldContainer) {
        const feedbackEl = dateFieldContainer.querySelector('.delivery-type-feedback');
        if (feedbackEl) {
          feedbackEl.remove();
        }
      }
    }
    
    // Also check other possible locations as fallback
    const deliverySuccess = this.querySelector('[ref="deliverySuccess"]');
    const deliveryError = this.querySelector('[ref="deliveryError"]');
    if (deliverySuccess && deliverySuccess.parentElement) {
      const feedbackEl = deliverySuccess.parentElement.querySelector('.delivery-type-feedback');
      if (feedbackEl) {
        feedbackEl.remove();
      }
    }
    if (deliveryError && deliveryError.parentElement) {
      const feedbackEl = deliveryError.parentElement.querySelector('.delivery-type-feedback');
      if (feedbackEl) {
        feedbackEl.remove();
      }
    }
  }

  /**
   * Update delivery type select options based on selected date
   * - If weekend date: show only Saturday option
   * - If non-weekend date: show Standard and Express options (hide Saturday)
   * - If no date: show Standard and Express (hide Saturday)
   * @param {string} selectedDate
   */
  updateDeliveryTypeOptions(selectedDate) {
    // Try multiple ways to find the select element
    // First try refs, then querySelector within the component, then within the modal dialog
    const modalRef = this.refs?.deliveryModal;
    const modal = (modalRef && !Array.isArray(modalRef) && modalRef instanceof HTMLElement) 
      ? modalRef 
      : (this.querySelector('[ref="deliveryModal"]') || this.querySelector('dialog.paving-order-modal'));
    
    const typeSelect = (this.refs?.deliveryTypeSelect && !Array.isArray(this.refs.deliveryTypeSelect) ? this.refs.deliveryTypeSelect : null) ||
                     (modal && modal instanceof HTMLElement ? modal.querySelector('[ref="deliveryTypeSelect"]') : null) ||
                     this.querySelector('[ref="deliveryTypeSelect"]') ||
                     this.querySelector('select[ref="deliveryTypeSelect"]') ||
                     (modal && modal instanceof HTMLElement ? modal.querySelector('.paving-order-modal__select') : null) ||
                     this.querySelector('.paving-order-modal__select');
    
    if (!typeSelect || !(typeSelect instanceof HTMLSelectElement)) {
      // Silently fail - element might not be ready yet
      return;
    }

    const isWeekend = selectedDate ? this.isWeekendDate(selectedDate) : false;
    const currentValue = typeSelect.value;

    // Get all options
    const standardOption = Array.from(typeSelect.options).find(opt => opt.value === 'standard');
    const expressOption = Array.from(typeSelect.options).find(opt => opt.value === 'express');
    const weekendOption = Array.from(typeSelect.options).find(opt => opt.value === 'weekend' || opt.value === 'saturday');

    if (isWeekend) {
      // Weekend date: show only Saturday, hide Standard and Express
      if (standardOption) standardOption.style.display = 'none';
      if (expressOption) expressOption.style.display = 'none';
      if (weekendOption) {
        weekendOption.style.display = '';
        // If currently selected option is hidden, switch to weekend
        if (currentValue === 'standard' || currentValue === 'express') {
          typeSelect.value = weekendOption.value;
          this.deliveryState.type = weekendOption.value;
        }
      }
    } else {
      // Non-weekend date or no date: show Standard and Express (if enabled), hide Saturday
      if (standardOption) standardOption.style.display = '';
      if (expressOption) {
        expressOption.style.display = this.expressAvailable ? '' : 'none';
        if (!this.expressAvailable && currentValue === 'express') {
          typeSelect.value = 'standard';
          this.deliveryState.type = 'standard';
        }
      }
      if (weekendOption) {
        weekendOption.style.display = 'none';
        // If currently selected option is hidden (weekend), switch to standard
        if (currentValue === 'weekend' || currentValue === 'saturday' || (weekendOption && currentValue === weekendOption.value)) {
          typeSelect.value = 'standard';
          this.deliveryState.type = 'standard';
        }
      }
    }
  }

  /**
   * Format price
   * @param {number} priceInPence
   */
  formatPrice(priceInPence) {
    const priceValue = typeof priceInPence === 'number' ? priceInPence : parseFloat(String(priceInPence)) || 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(priceValue / 100);
  }

  /**
   * Calculate delivery cost per pack
   * @param {string} type
   * @param {string} zone
   * @param {string} date
   * @returns {number} Delivery cost per pack in pence
   */
  calculateDeliveryCostPerPack(type, zone, date) {
    return this.estimateDeliveryCost(type, zone, date, 1); // Calculate for 1 pack
  }

  /**
   * Update delivery display
   */
  updateDeliveryDisplay() {
    if (!this.selectedVariant) return;
    
    const unitPrice = this.selectedVariant.price || 0;
    const productCost = unitPrice * this.quantity;
    
    // Update quantity display
    const summaryQuantityDisplay = this.querySelector('[ref="summaryQuantityDisplay"]') || 
                                   (this.refs?.summaryQuantityDisplay && !Array.isArray(this.refs.summaryQuantityDisplay) ? this.refs.summaryQuantityDisplay : null);
    if (summaryQuantityDisplay && summaryQuantityDisplay instanceof HTMLElement) {
      summaryQuantityDisplay.textContent = String(this.quantity);
    }
    
    // Update unit price display
    const summaryUnitPriceDisplay = this.querySelector('[ref="summaryUnitPriceDisplay"]') || 
                                    (this.refs?.summaryUnitPriceDisplay && !Array.isArray(this.refs.summaryUnitPriceDisplay) ? this.refs.summaryUnitPriceDisplay : null);
    if (summaryUnitPriceDisplay && summaryUnitPriceDisplay instanceof HTMLElement) {
      summaryUnitPriceDisplay.textContent = this.formatPrice(unitPrice);
    }
    
    // Update product price in summary section (always show if variant is selected)
    const summaryProductPrice = this.querySelector('[ref="summaryProductPriceDisplay"]') || 
                                (this.refs?.summaryProductPriceDisplay && !Array.isArray(this.refs.summaryProductPriceDisplay) ? this.refs.summaryProductPriceDisplay : null);
    if (summaryProductPrice && summaryProductPrice instanceof HTMLElement) {
      summaryProductPrice.textContent = this.formatPrice(productCost);
    }
    
    // Update delivery cost per pack and total delivery cost
    const deliveryCostPerPackDisplay = this.querySelector('[ref="deliveryCostPerPackDisplay"]') || 
                                       (this.refs?.deliveryCostPerPackDisplay && !Array.isArray(this.refs.deliveryCostPerPackDisplay) ? this.refs.deliveryCostPerPackDisplay : null);
    const deliveryQuantityDisplay = this.querySelector('[ref="deliveryQuantityDisplay"]') || 
                                    (this.refs?.deliveryQuantityDisplay && !Array.isArray(this.refs.deliveryQuantityDisplay) ? this.refs.deliveryQuantityDisplay : null);
    const deliveryDisplay = this.querySelector('[ref="deliveryCostDisplay"]') || 
                           (this.refs?.deliveryCostDisplay && !Array.isArray(this.refs.deliveryCostDisplay) ? this.refs.deliveryCostDisplay : null);
    
    if (this.deliveryState.zone && this.deliveryState.postcode && this.deliveryState.date) {
      // Calculate delivery cost per pack
      const deliveryCostPerPack = this.calculateDeliveryCostPerPack(this.deliveryState.type, this.deliveryState.zone, this.deliveryState.date);
      
      // For weekend delivery, it's a flat rate (£75) regardless of quantity
      // For other types, it's per-pack pricing
      const isWeekend = this.deliveryState.type === 'weekend' || this.deliveryState.type === 'saturday';
      let totalDeliveryCost;
      let displayQuantity;
      
      if (isWeekend) {
        // Weekend: flat rate, always £75 regardless of quantity
        totalDeliveryCost = deliveryCostPerPack; // Already £75 flat
        displayQuantity = 1; // Always show as 1 for flat rate
      } else {
        // Standard/Express: per-pack pricing
        totalDeliveryCost = deliveryCostPerPack * this.quantity;
        displayQuantity = this.quantity;
      }
      
      this.deliveryState.cost = totalDeliveryCost;
      
      // Update delivery quantity display
      if (deliveryQuantityDisplay && deliveryQuantityDisplay instanceof HTMLElement) {
        deliveryQuantityDisplay.textContent = String(displayQuantity);
      }
      
      // Update delivery cost per pack display
      if (deliveryCostPerPackDisplay && deliveryCostPerPackDisplay instanceof HTMLElement) {
        if (isWeekend) {
          // For weekend, show "Flat rate" or just the price
          deliveryCostPerPackDisplay.textContent = this.formatPrice(deliveryCostPerPack);
        } else {
          // For per-pack, show per-pack price
          deliveryCostPerPackDisplay.textContent = deliveryCostPerPack > 0 
            ? this.formatPrice(deliveryCostPerPack) 
            : 'Free';
        }
      }
      
      // Update total delivery cost display
      if (deliveryDisplay && deliveryDisplay instanceof HTMLElement) {
        deliveryDisplay.textContent = totalDeliveryCost > 0 
          ? this.formatPrice(totalDeliveryCost) 
          : 'Free';
      }
    } else {
      // Reset displays if delivery not calculated
      if (deliveryQuantityDisplay && deliveryQuantityDisplay instanceof HTMLElement) {
        deliveryQuantityDisplay.textContent = '—';
      }
      if (deliveryCostPerPackDisplay && deliveryCostPerPackDisplay instanceof HTMLElement) {
        deliveryCostPerPackDisplay.textContent = '—';
      }
      if (deliveryDisplay && deliveryDisplay instanceof HTMLElement) {
        deliveryDisplay.textContent = '—';
      }
    }
    
    // Update total
    const totalDisplay = this.querySelector('[ref="totalCostDisplay"]') || 
                        (this.refs?.totalCostDisplay && !Array.isArray(this.refs.totalCostDisplay) ? this.refs.totalCostDisplay : null);
    if (totalDisplay && totalDisplay instanceof HTMLElement) {
      if (this.deliveryState.zone && this.deliveryState.postcode && this.deliveryState.date) {
        const total = productCost + this.deliveryState.cost;
        totalDisplay.textContent = this.formatPrice(total);
      } else {
        // Show product price as total if delivery not calculated yet
        totalDisplay.textContent = this.formatPrice(productCost);
      }
    }
    
    // Enable/disable add to cart button
    const addToCartBtn = this.querySelector('[ref="addToCartBtn"]') || 
                        (this.refs?.addToCartBtn && !Array.isArray(this.refs.addToCartBtn) ? this.refs.addToCartBtn : null);
    if (addToCartBtn && addToCartBtn instanceof HTMLButtonElement) {
      const canAdd = this.deliveryState.zone && 
                     this.deliveryState.zone !== 'Zone 4' && 
                     this.deliveryState.postcode &&
                     this.deliveryState.date &&
                     this.selectedVariant;
      addToCartBtn.disabled = !canAdd;
      addToCartBtn.classList.toggle('is-disabled', !canAdd);
    }
  }

  /**
   * Set delivery error
   * @param {string} message
   */
  setDeliveryError(message) {
    const errorEl = this.refs.deliveryError;
    if (errorEl && errorEl instanceof HTMLElement) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
    const successEl = this.refs.deliverySuccess;
    if (successEl && successEl instanceof HTMLElement) {
      successEl.style.display = 'none';
    }
  }

  /**
   * Set delivery success
   * @param {string} message
   */
  setDeliverySuccess(message) {
    const successEl = this.refs.deliverySuccess;
    if (successEl && successEl instanceof HTMLElement) {
      successEl.textContent = message;
      successEl.style.display = 'block';
    }
    const errorEl = this.refs.deliveryError;
    if (errorEl && errorEl instanceof HTMLElement) {
      errorEl.style.display = 'none';
    }
  }

  /**
   * Add to cart
   */
  async addToCart() {
    if (!this.selectedVariant) {
      this.setDeliveryError('Please select a pallet type first.');
      return;
    }
    
    if (!this.deliveryState.zone || !this.deliveryState.postcode || !this.deliveryState.date) {
      this.setDeliveryError('Please complete delivery details first.');
      return;
    }
    
    // Re-validate date (min date, holiday, Sunday) so invalid date cannot be submitted
    if (!this.validateDeliveryDate(this.deliveryState.date)) {
      this.updateDeliveryDisplay();
      return;
    }
    
    // Validate delivery date is not a blocked holiday
    if (this.deliveryState.date) {
      const holidayCheck = this.isDateBlocked(this.deliveryState.date);
      if (holidayCheck.blocked) {
        const errorMessage = `Delivery is not available on ${holidayCheck.holidayName}. Please select another date.`;
        this.setDeliveryError(errorMessage);
        this.showDeliveryDateError(errorMessage);
        
        // Focus date input
        const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                         this.querySelector('input[type="date"]') ||
                         (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
        if (dateInput && dateInput instanceof HTMLInputElement) {
          dateInput.focus();
          if (typeof dateInput.showPicker === 'function') {
            dateInput.showPicker();
          }
        }
        
        // Disable add to cart button
        const addToCartBtn = this.querySelector('[ref="addToCartBtn"]') || 
                            (this.refs?.addToCartBtn && !Array.isArray(this.refs.addToCartBtn) ? this.refs.addToCartBtn : null);
        if (addToCartBtn && addToCartBtn instanceof HTMLButtonElement) {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('is-disabled');
        }
        return;
      }
      
      // Also check for Sunday
      const dayOfWeek = this.getDayOfWeek(this.deliveryState.date);
      if (dayOfWeek === 0) {
        const errorMessage = 'Sunday delivery is not available. Please select a different date.';
        this.setDeliveryError(errorMessage);
        this.showDeliveryDateError(errorMessage);
        
        // Focus date input
        const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                         this.querySelector('input[type="date"]') ||
                         (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
        if (dateInput && dateInput instanceof HTMLInputElement) {
          dateInput.focus();
          if (typeof dateInput.showPicker === 'function') {
            dateInput.showPicker();
          }
        }
        
        // Disable add to cart button
        const addToCartBtn = this.querySelector('[ref="addToCartBtn"]') || 
                            (this.refs?.addToCartBtn && !Array.isArray(this.refs.addToCartBtn) ? this.refs.addToCartBtn : null);
        if (addToCartBtn && addToCartBtn instanceof HTMLButtonElement) {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('is-disabled');
        }
        return;
      }
    }
    
    // Zone 4 - Block add to cart and checkout
    if (this.deliveryState.zone === 'Zone 4') {
      this.setDeliveryError('Delivery not available to this area. Please contact us for shipping options.');
      
      // Ensure add to cart is disabled
      const addToCartBtn = this.querySelector('[ref="addToCartBtn"]') || 
                          (this.refs?.addToCartBtn && !Array.isArray(this.refs.addToCartBtn) ? this.refs.addToCartBtn : null);
      if (addToCartBtn && addToCartBtn instanceof HTMLButtonElement) {
        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('is-disabled');
      }
      
      // Also prevent checkout globally
      this.preventCheckoutForZone4();
      return;
    }
    
    // Show loading state
    this.setAddToCartLoading(true);
    
    const deliveryVariantId = this.getDeliveryVariantId(this.deliveryState.zone, this.deliveryState.type);
    
    /** @type {any[]} */
    const items = [{
      id: this.selectedVariant.id,
      quantity: this.quantity,
      properties: {
        'Pack Type': this.selectedPackType ? this.selectedPackType.charAt(0).toUpperCase() + this.selectedPackType.slice(1) + ' pack' : '',
        'Delivery Zone': this.deliveryState.zone,
        'Delivery Type': this.deliveryState.type.charAt(0).toUpperCase() + this.deliveryState.type.slice(1),
        'Delivery Date': this.deliveryState.date,
        '_delivery_zone': this.deliveryState.zone,
        '_delivery_type': this.deliveryState.type,
        '_delivery_date': this.deliveryState.date
      }
    }];
    
    if (deliveryVariantId) {
      // Get the delivery variant to check its price
      const deliveryVariant = this.deliveryVariants.find(v => v.id === deliveryVariantId);
      const variantPrice = deliveryVariant?.price || 0; // Price in pence (can be 0 for free delivery)
      
      // Calculate delivery quantity to match calculated cost
      // For Saturday delivery (£75 flat), quantity is ALWAYS 1 (flat rate, not affected by pack quantity)
      // For other types (per-pack pricing), quantity should match pack count
      // For free delivery (Zone 1 Standard), quantity matches pack count
      const isWeekend = this.deliveryState.type === 'weekend' || this.deliveryState.type === 'saturday';
      const isFreeDelivery = this.deliveryState.cost === 0;
      let deliveryQuantity;
      
      if (isWeekend) {
        // Weekend delivery: Always quantity 1 (flat rate £75, not per-pack)
        deliveryQuantity = 1;
      } else if (isFreeDelivery) {
        // Free delivery (Zone 1 Standard): quantity matches pack count
        deliveryQuantity = this.quantity;
      } else {
        // Standard/Express with cost: Per-pack pricing, quantity matches pack count
        if (variantPrice > 0) {
          // Try to match variant price × quantity = calculated cost
          const calculatedQuantity = Math.round(this.deliveryState.cost / variantPrice);
          if (calculatedQuantity > 0 && Math.abs((calculatedQuantity * variantPrice) - this.deliveryState.cost) < 1) {
            // Price matches when multiplied by calculated quantity (within 1 pence tolerance)
            deliveryQuantity = calculatedQuantity;
          } else {
            // Fallback: use pack count (this.quantity) for per-pack pricing
            deliveryQuantity = this.quantity;
          }
        } else {
          deliveryQuantity = this.quantity;
        }
      }
      
      items.push({
        id: deliveryVariantId,
        quantity: deliveryQuantity,
        properties: {
          'Delivery': `${this.deliveryState.zone} / ${this.deliveryState.type.charAt(0).toUpperCase() + this.deliveryState.type.slice(1)}`,
          'Delivery Zone': this.deliveryState.zone,
          'Delivery Type': this.deliveryState.type.charAt(0).toUpperCase() + this.deliveryState.type.slice(1),
          'Delivery Date': this.deliveryState.date,
          'Pack Count': String(this.quantity),
          '_delivery_zone': this.deliveryState.zone,
          '_delivery_type': this.deliveryState.type,
          '_delivery_date': this.deliveryState.date,
          '_calculated_delivery_cost_pence': String(this.deliveryState.cost),
          '_delivery_quantity': String(deliveryQuantity),
          '_pack_count': String(this.quantity),
          '_variant_price_pence': String(variantPrice),
          ...(isFreeDelivery ? { '_free_delivery': 'true' } : {})
        }
      });
    }
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add to cart');
      }
      
      // Refresh cart and open drawer
      const cartRes = await fetch('/cart.js');
      const cartJson = await cartRes.json();
      
      document.dispatchEvent(new CartAddEvent(cartJson, 'paving-order-flow', {
        source: 'paving-order-flow',
        itemCount: cartJson.item_count
      }));
      
      // Open cart drawer
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
        (/** @type {any} */ (cartDrawer)).open();
      }
      
      this.closeDeliveryModal();
    } catch (error) {
      console.error('Add to cart failed', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add to cart. Please try again.';
      this.setDeliveryError(errorMessage);
    } finally {
      this.setAddToCartLoading(false);
    }
  }

  /**
   * Set add to cart loading state
   * @param {boolean} isLoading
   */
  setAddToCartLoading(isLoading) {
    const addToCartBtn = this.refs.addToCartBtn;
    if (addToCartBtn && addToCartBtn instanceof HTMLButtonElement) {
      addToCartBtn.disabled = isLoading;
      addToCartBtn.classList.toggle('is-loading', isLoading);
      
      if (isLoading) {
        addToCartBtn.textContent = 'Adding to cart...';
      } else {
        addToCartBtn.textContent = 'Add to cart';
      }
    }
  }

  /**
   * Close modals
   */
  closeOrderTypeModal() {
    const modal = this.refs.orderTypeModal;
    if (modal && modal instanceof HTMLDialogElement) {
      modal.close();
    }
  }

  closePackTypeModal() {
    const modal = this.refs.packTypeModal;
    if (modal && modal instanceof HTMLDialogElement) {
      modal.close();
    }
  }

  closeDeliveryModal() {
    const modal = this.refs.deliveryModal;
    if (modal && modal instanceof HTMLDialogElement) {
      modal.close();
    }
  }

  /**
   * Back to order type selection
   */
  backToOrderType() {
    this.closePackTypeModal();
    this.showOrderTypeSelection();
  }

  /**
   * Back to pack type selection
   */
  backToPackType() {
    this.closeDeliveryModal();
    this.showPackTypeSelection();
  }

  /**
   * Open calculator (redirect to existing calculator)
   */
  openCalculator() {
    // Scroll to calculator or trigger calculator modal
    const calculatorTrigger = document.querySelector('[data-calculator-trigger]');
    if (calculatorTrigger && calculatorTrigger instanceof HTMLElement) {
      calculatorTrigger.click();
    } else {
      // Fallback: scroll to calculator section
      const calculatorSection = document.querySelector('product-calculator-component');
      if (calculatorSection) {
        calculatorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  /**
   * Parse blocked delivery holidays from store metafield
   * Format: "2026-01-01 | New Year's Day\n2026-04-18 | Good Friday"
   */
  parseBlockedHolidays() {
    this.blockedHolidays.clear();
    
    const holidaysData = this.dataset.deliveryHolidays || '';
    if (!holidaysData || holidaysData.trim() === '') {
      return;
    }
    
    // Decode HTML entities if present
    let decodedData = holidaysData;
    if (decodedData.includes('&quot;')) {
      decodedData = decodedData.replace(/&quot;/g, '"');
    }
    if (decodedData.includes('&#39;')) {
      decodedData = decodedData.replace(/&#39;/g, "'");
    }
    if (decodedData.includes('&amp;')) {
      decodedData = decodedData.replace(/&amp;/g, '&');
    }
    
    // Split by newlines and parse each line
    const lines = decodedData.split(/\r?\n/);
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // Parse format: "YYYY-MM-DD | Holiday Name"
      const parts = trimmed.split('|').map(p => p.trim());
      if (parts.length >= 2) {
        const dateStr = parts[0] || '';
        const holidayName = parts.slice(1).join('|').trim(); // Rejoin in case holiday name contains |
        
        // Validate date format (YYYY-MM-DD)
        if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          this.blockedHolidays.set(dateStr, holidayName || 'Holiday');
        }
      }
    }
  }

  /**
   * Check if a date is blocked (holiday)
   * @param {string} dateStr - Date in YYYY-MM-DD format
   * @returns {{blocked: boolean, holidayName?: string}}
   */
  isDateBlocked(dateStr) {
    if (!dateStr) return { blocked: false };
    
    const holidayName = this.blockedHolidays.get(dateStr);
    if (holidayName) {
      return { blocked: true, holidayName };
    }
    
    return { blocked: false };
  }

  /**
   * Get day of week (0 = Sunday, 1 = Monday, etc.)
   * @param {string} dateStr - Date in YYYY-MM-DD format
   * @returns {number}
   */
  getDayOfWeek(dateStr) {
    if (!dateStr) return -1;
    const date = this.parseDate(dateStr);
    if (!date) return -1;
    return date.getDay();
  }

  /**
   * Parse date string to Date object
   * @param {string} dateStr
   * @returns {Date|null}
   */
  parseDate(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  /**
   * Validate delivery date (prevent Sundays and blocked holidays)
   * @param {string} dateStr
   * @returns {boolean}
   */
  validateDeliveryDate(dateStr) {
    if (!dateStr) return true;
    
    // Enforce minimum date (iOS Safari often ignores input min attribute)
    const minDates = this.calculateMinimumDeliveryDates();
    const selectedObj = this.parseDate(dateStr);
    const minBoundaryStr = this.expressAvailable ? minDates.express : minDates.standard;
    const minObj = this.parseDate(minBoundaryStr);
    if (selectedObj && minObj && selectedObj < minObj) {
      const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                       this.querySelector('input[type="date"]') ||
                       (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
      if (dateInput && dateInput instanceof HTMLInputElement) {
        dateInput.value = '';
      }
      this.deliveryState.date = '';
      this.updateDeliveryDisplay();
      const minFormatted = minObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const errorMessage = `The selected date is too soon. The earliest delivery date is ${minFormatted}.`;
      this.setDeliveryError(errorMessage);
      this.showDeliveryDateError(errorMessage);
      return false;
    }
    
    // Check if date is a blocked holiday
    const holidayCheck = this.isDateBlocked(dateStr);
    if (holidayCheck.blocked) {
      const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                       this.querySelector('input[type="date"]') ||
                       (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
      if (dateInput && dateInput instanceof HTMLInputElement) {
        dateInput.value = '';
      }
      this.deliveryState.date = '';
      this.updateDeliveryDisplay();
      const errorMessage = `Delivery is not available on ${holidayCheck.holidayName}. Please select another date.`;
      this.setDeliveryError(errorMessage);
      this.showDeliveryDateError(errorMessage);
      return false;
    }
    
    const dayOfWeek = this.getDayOfWeek(dateStr);
    if (dayOfWeek === 0) { // Sunday
      const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                       this.querySelector('input[type="date"]') ||
                       (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
      if (dateInput && dateInput instanceof HTMLInputElement) {
        dateInput.value = '';
      }
      this.deliveryState.date = '';
      this.updateDeliveryDisplay();
      const errorMessage = 'Sunday delivery is not available. Please select a different date.';
      this.setDeliveryError(errorMessage);
      this.showDeliveryDateError(errorMessage);
      return false;
    }
    
    // Clear any previous error if date is valid (inline + main error panel)
    this.clearDeliveryDateError();
    const errorEl = this.refs?.deliveryError;
    if (errorEl && errorEl instanceof HTMLElement) {
      errorEl.style.display = 'none';
    }
    return true;
  }

  /**
   * Show error message near the date picker
   * @param {string} message
   */
  showDeliveryDateError(message) {
    const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                     this.querySelector('input[type="date"]') ||
                     (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
    if (!dateInput || !(dateInput instanceof HTMLInputElement)) return;
    
    // Remove existing error if present
    this.clearDeliveryDateError();
    
    // Create error element
    const errorEl = document.createElement('div');
    errorEl.className = 'paving-order-modal__delivery-date-error';
    errorEl.setAttribute('role', 'alert');
    errorEl.style.cssText = 'color: #d32f2f; font-size: 0.875rem; margin-top: 0.5rem; padding: 0.5rem; background-color: #ffebee; border: 1px solid #d32f2f; border-radius: 4px;';
    errorEl.textContent = message;
    
    // Insert after the date input's parent container or the input itself
    const dateField = dateInput.closest('.paving-order-modal__field');
    if (dateField) {
      dateField.appendChild(errorEl);
    } else {
      dateInput.parentElement?.insertBefore(errorEl, dateInput.nextSibling);
    }
    
    // Add error class to input
    dateInput.classList.add('has-error');
    dateInput.setAttribute('aria-invalid', 'true');
    dateInput.setAttribute('aria-describedby', errorEl.id || 'delivery-date-error');
    if (!errorEl.id) {
      errorEl.id = `delivery-date-error-${Date.now()}`;
      dateInput.setAttribute('aria-describedby', errorEl.id);
    }
  }

  /**
   * Clear error message from date picker
   */
  clearDeliveryDateError() {
    const dateInput = this.querySelector('[ref="deliveryDateInput"]') ||
                     this.querySelector('input[type="date"]') ||
                     (this.refs?.deliveryDateInput && !Array.isArray(this.refs.deliveryDateInput) ? this.refs.deliveryDateInput : null);
    if (dateInput && dateInput instanceof HTMLInputElement) {
      dateInput.classList.remove('has-error');
      dateInput.removeAttribute('aria-invalid');
      dateInput.removeAttribute('aria-describedby');
    }
    
    const errorEl = this.querySelector('.paving-order-modal__delivery-date-error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  /**
   * Prevent checkout for Zone 4
   */
  preventCheckoutForZone4() {
    // Disable checkout buttons
    const checkoutButtons = document.querySelectorAll(
      'a[href*="/checkout"], button[data-checkout], .cart__checkout, [data-cart-checkout], #checkout, button[name="checkout"]'
    );
    
    checkoutButtons.forEach(button => {
      if (button instanceof HTMLAnchorElement) {
        const anchor = button;
        anchor.style.pointerEvents = 'none';
        anchor.style.opacity = '0.5';
        anchor.style.cursor = 'not-allowed';
        anchor.setAttribute('aria-disabled', 'true');
        anchor.setAttribute('data-zone4-blocked', 'true');
        anchor.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }, true);
      } else if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-zone4-blocked', 'true');
      }
    });
    
    // Show warning message in cart
    let warningEl = document.querySelector('.cart__zone4-message, [data-zone4-message]');
    if (!warningEl || !(warningEl instanceof HTMLElement)) {
      const warningDiv = document.createElement('div');
      warningDiv.className = 'cart__zone4-message';
      warningDiv.setAttribute('data-zone4-message', 'true');
      warningDiv.style.cssText = 'padding: 1rem; margin: 1rem 0; background-color: #ffebee; border: 1px solid #d32f2f; border-radius: 4px; color: #d32f2f; font-weight: 500;';
      
      const cartSummary = document.querySelector('.cart-drawer__summary, .cart__summary, [data-cart-summary]');
      const checkoutBtn = document.querySelector('a[href*="/checkout"], button[data-checkout], .cart__checkout');
      
      if (checkoutBtn && checkoutBtn.parentElement) {
        checkoutBtn.parentElement.insertBefore(warningDiv, checkoutBtn);
      } else if (cartSummary && cartSummary instanceof HTMLElement) {
        cartSummary.insertBefore(warningDiv, cartSummary.firstChild);
      }
      
      warningEl = warningDiv;
    }
    
    if (warningEl instanceof HTMLElement) {
      warningEl.textContent = 'Delivery not available to your area. Please contact us for shipping options.';
      warningEl.style.display = 'block';
    }
  }
}

customElements.define('paving-order-flow', PavingOrderFlow);

// Global function to open order type selection
if (typeof window !== 'undefined') {
  (/** @type {any} */ (window)).openOrderTypeSelection = function(/** @type {number} */ productId) {
    const flow = document.querySelector(`paving-order-flow[data-product-id="${productId}"]`);
    if (flow && 'showOrderTypeSelection' in flow && typeof (/** @type {any} */ (flow)).showOrderTypeSelection === 'function') {
      (/** @type {any} */ (flow)).showOrderTypeSelection();
    }
  };
}

